// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LevelBand, { LevelEntryType } from "./LevelBand";
import { TrialEntryType } from "./TrialCard";
import { RUNGS } from "../SummarySection/StereoLadderChart";
import SummaryCard from "../../shared/SummaryCard";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

type ModulesType = NonNullable<
  ExamSectionReportType["postProcessing"]["modules"]
>;
export type StereoLadderModuleType = ModulesType["StereoLadder"];

type TrialReviewSectionType = {
  stereoLadder: StereoLadderModuleType;
};

// Every trial the algorithm presents, in order. Newer reports say which rung each trial belongs to
// (levelNumber, 1-based into the ladder), catch trials included. Before that a catch trial carries
// no disparity of its own (0″), so it joins the band of whichever rung trial comes next -- the
// only rung a catch trial ever interrupts is the one either side of it.
const assignBands = (
  trials: Array<TrialEntryType>,
  ladder: Array<number>
): Array<TrialEntryType & { band: number }> => {
  const withBand: Array<TrialEntryType & { band: number }> = new Array(
    trials.length
  );
  let nextRungDisparity: number | null = null;
  for (let i = trials.length - 1; i >= 0; i -= 1) {
    const trial = trials[i];
    if (trial.kind !== "Catch") {
      nextRungDisparity = trial.disparityArcsec;
    }
    const levelRung =
      trial.levelNumber !== undefined
        ? ladder[trial.levelNumber - 1]
        : undefined;
    withBand[i] = {
      ...trial,
      band:
        levelRung ??
        (trial.kind !== "Catch"
          ? trial.disparityArcsec
          : nextRungDisparity ?? trial.disparityArcsec),
    };
  }
  return withBand;
};

// The audit trail behind the summary's threshold: every rung and catch trial the patient saw,
// grouped by disparity, with the picture they were shown and the one they picked.
const TrialReviewSection = ({ stereoLadder }: TrialReviewSectionType) => {
  const { t } = useTranslation();

  const trials = stereoLadder?.trials;
  const ladderArcsec = stereoLadder?.ladderArcsec;
  const ladder = useMemo(
    () => (ladderArcsec && ladderArcsec.length > 0 ? ladderArcsec : RUNGS),
    [ladderArcsec]
  );
  const bands = useMemo(
    () => (trials ? assignBands(trials, ladder) : []),
    [trials, ladder]
  );

  const levelsByDisparity = useMemo(() => {
    const map = new Map<number, LevelEntryType>();
    (stereoLadder?.levels ?? []).forEach((level) => {
      map.set(level.disparityArcsec, level);
    });
    return map;
  }, [stereoLadder?.levels]);

  if (!stereoLadder || !trials || trials.length === 0) return null;

  const catchTrials = stereoLadder.catchTrials;
  const thresholdValue = stereoLadder.thresholdArcsec?.value ?? null;

  return (
    <>
      <SummaryCard title={t("What the Patient Saw and Picked")} cautions={null}>
        {catchTrials && catchTrials.presented === 0 && (
          <Typography fontSize="12px" color="#5B6D7A" pb="8px">
            {t("No catch trials were presented in this run.")}
          </Typography>
        )}
        {catchTrials && catchTrials.presented > 0 && (
          <Typography
            fontSize="12px"
            color={catchTrials.validityPassed ? "#5B6D7A" : "#E10C0C"}
            pb="8px"
          >
            {t(
              "Catch trials: {{correct}} of {{presented}} correctly answered with None",
              {
                correct: catchTrials.correct,
                presented: catchTrials.presented,
              }
            )}
            {!catchTrials.validityPassed &&
              ` — ${t(
                "below the 75% needed. The patient picked a shape when none had depth, so read the threshold as unreliable."
              )}`}
          </Typography>
        )}
      </SummaryCard>

      <Box display="flex" flexDirection="column" gap="8px">
        {ladder.map((rung, index) => (
          <LevelBand
            key={rung}
            rung={rung}
            levelIndex={index + 1}
            level={levelsByDisparity.get(rung)}
            trials={bands.filter((trial) => trial.band === rung)}
            isThreshold={thresholdValue === rung}
          />
        ))}
      </Box>
    </>
  );
};

export default TrialReviewSection;
